/**
 * AssigneePicker — выбор исполнителей (teams_implementation_plan.md, Этап 3).
 *
 * Было: плоский список высоких карточек всех сотрудников (~1000px при 20
 * людях), выбор — визуальный поиск человека глазами.
 * Стало: поле с чипами выбранных (одна строка, сколько бы людей ни было в
 * орге) + «Назначить», открывающее панель:
 *   1. поиск (имя / телефон / email / команда);
 *   2. «Рекомендованы» — кто уже работал на этом Объекте;
 *   3. команды с «Всю команду» одним тапом;
 *   4. «Без команды».
 * На телефоне панель — шторка снизу, на десктопе — раскрывается под полем.
 *
 * Список людей — GET /api/team/assignable: сервер уже отрезал то, что
 * пользователю назначать нельзя (Бригадир — только свои команды), и той же
 * функцией проверяет assignee_ids при сохранении. UI ничего не фильтрует сам.
 *
 * Уже назначенные, которых нет в списке (Админ поставил человека из чужой
 * бригады, а открыл Бригадир), остаются чипами из knownUsers — их можно
 * снять, но нельзя добавить заново.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { fetchAssignable } from '../hooks/useAssignable';
import { track } from '../analytics';
import { useAuth } from '../hooks/useAuth';
import useIsNarrow from '../hooks/useIsNarrow';
import useKeyboardFit, { keyboardFitStyle } from '../hooks/useKeyboardFit';
import useEscape from '../hooks/useEscape';
import { focusQuiet, revealRow } from '../utils/scroll';
import { backdropClose } from '../utils/modalBackdrop';
import { groupMembersByTeam, normalizeQuery, memberMatches } from '../utils/teamGrouping';
import UserCircle from './UserCircle';
import Icon from './Icon';

const FIELD = { height: 36, minHeight: 36, maxHeight: 36, fontSize: 13, paddingTop: 0, paddingBottom: 0 };
// Больше — секции команд по умолчанию свёрнуты (кроме тех, где уже есть выбранные)
const EXPAND_ALL_UP_TO = 15;

function roleText(t, i18n, u) {
  const en = String(i18n.language).startsWith('en');
  const own = en ? (u.role_name_en || u.role_name) : (u.role_name || u.role_name_en);
  return own || (u.role_key ? t(`perm.role.${u.role_key}`, u.role_key) : (u.role || ''));
}

/** Строка человека в панели. Верхнеуровневый компонент, не вложенный. */
function PersonRow({ u, selected, onToggle, sub, isMe, t, i18n, indent }) {
  return (
    <button type="button" onClick={onToggle}
      // Без фокуса по мыши: браузер подкручивал модалку к сфокусированной
      // строке, и следующий тап попадал в соседнего человека.
      onMouseDown={(e) => e.preventDefault()}
      aria-pressed={selected}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
        padding: `8px 12px 8px ${indent ? 28 : 12}px`, border: 'none', borderBottom: '1px solid var(--border)',
        background: selected ? 'rgba(79,142,247,0.08)' : 'transparent', cursor: 'pointer', color: 'var(--text)',
      }}>
      <span aria-hidden="true" style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
        background: selected ? 'var(--accent)' : 'transparent', color: '#fff',
      }}>{selected && <Icon name="check" size={12} />}</span>
      <UserCircle name={u.name} avatarUrl={u.avatar_thumb_url} size={28} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {u.name}{isMe && <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {t('assignee.you')}</span>}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sub || roleText(t, i18n, u)}
        </span>
      </span>
    </button>
  );
}

export default function AssigneePicker({ value = [], onChange, propertyId = null, knownUsers = [], onLoaded }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const narrow = useIsNarrow();
  const kb = useKeyboardFit();   // шторка на телефоне — над клавиатурой, а не под ней
  const [data, setData] = useState(null); // { users, teams, recent_user_ids }
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(null); // Set ключей; null — ещё не инициализировано
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let cancelled = false;
    fetchAssignable(propertyId)
      .then((d) => { if (!cancelled) { setData(d); onLoadedRef.current?.(d); } })
      .catch(() => { if (!cancelled) setData({ users: [], teams: [], recent_user_ids: [] }); });
    return () => { cancelled = true; };
  }, [propertyId]);

  useEscape(open, () => setOpen(false));
  useEffect(() => {
    // На телефоне клавиатура закрывала бы половину шторки — фокус только на десктопе
    if (!open || narrow) return undefined;
    focusQuiet(searchRef.current);
    // Панель раскрывается под полем внизу формы — дотягиваем её в зону
    // видимости минимальным движением, а не заставляем листать руками.
    const id = requestAnimationFrame(() => revealRow(panelRef.current));
    return () => cancelAnimationFrame(id);
  }, [open, narrow]);

  const users = useMemo(() => (data?.users || []), [data]);
  const teams = useMemo(() => (data?.teams || []), [data]);
  const byId = useMemo(() => {
    const m = new Map();
    for (const k of knownUsers || []) if (k && k.id) m.set(k.id, k);
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users, knownUsers]);
  const selected = useMemo(() => new Set(value), [value]);
  const q = normalizeQuery(query);

  const setIds = (ids) => onChange([...new Set(ids)]);
  const toggle = (id, source) => {
    const on = !selected.has(id);
    setIds(on ? [...value, id] : value.filter((x) => x !== id));
    track('assignee_toggled', { action: on ? 'add' : 'remove', source });
  };

  const openPanel = () => {
    setOpen(true);
    setQuery('');
    if (collapsed === null) {
      // Мало людей — всё раскрыто. Много — раскрыты только секции с выбранными.
      const keys = new Set();
      if (users.length > EXPAND_ALL_UP_TO) {
        for (const tm of teams) if (!users.some((u) => u.team_id === tm.id && selected.has(u.id))) keys.add(`t${tm.id}`);
        if (!users.some((u) => !u.team_id && selected.has(u.id))) keys.add('none');
      }
      setCollapsed(keys);
    }
    track('assignee_picker_opened', {
      available: users.length, teams: teams.length, recent: (data?.recent_user_ids || []).length, selected: value.length,
    });
  };

  const toggleSection = (key) => setCollapsed((prev) => {
    const next = new Set(prev || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const bulk = (ids, teamId) => {
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    setIds(allOn ? value.filter((id) => !ids.includes(id)) : [...value, ...ids]);
    track('assignee_team_bulk', { action: allOn ? 'remove' : 'add', size: ids.length, team: teamId ? 'team' : 'none' });
  };

  // ── Поле с чипами ─────────────────────────────────────────────────────────
  const chips = value.map((id) => byId.get(id)).filter(Boolean);
  const field = (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '5px 6px', minHeight: 36, boxSizing: 'border-box',
      border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg3)',
    }}>
      {chips.map((u) => (
        <span key={u.id} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 4px 0 2px', borderRadius: 999,
          background: 'rgba(79,142,247,0.12)', color: 'var(--text)', fontSize: 13, maxWidth: '100%',
        }}>
          <UserCircle name={u.name} avatarUrl={u.avatar_thumb_url} size={20} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{u.name}</span>
          <button type="button" aria-label={t('assignee.remove', { name: u.name })} data-ph-capture="assignee_chip_remove"
            onClick={() => toggle(u.id, 'chip')}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, color: 'var(--text2)', display: 'inline-flex' }}>
            <Icon name="x" size={12} />
          </button>
        </span>
      ))}
      <button type="button" data-ph-capture="assignee_picker_open" disabled={!data}
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
          color: 'var(--accent)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: '0 6px', height: 24,
        }}>
        <Icon name={open ? 'chevronDown' : 'plus'} size={14} />
        {chips.length ? t('assignee.add') : t('assignee.assign')}
      </button>
    </div>
  );

  // ── Панель ────────────────────────────────────────────────────────────────
  const recentIds = (data?.recent_user_ids || []).filter((id) => byId.has(id) && users.some((u) => u.id === id));
  const grouped = groupMembersByTeam(users, teams, '');
  const isOpenSection = (key) => !(collapsed || new Set()).has(key);

  const sectionHead = (key, title, ids, teamId, lead) => {
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    const openS = isOpenSection(key);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 6px 4px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
        <button type="button" onClick={() => toggleSection(key)} aria-expanded={openS}
          aria-label={openS ? t('teams.collapse') : t('teams.expand')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text)', flex: 1, minWidth: 0, textAlign: 'left', padding: '2px 4px' }}>
          <Icon name={openS ? 'chevronDown' : 'chevronRight'} size={14} />
          <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          <span style={{ fontSize: 12, color: 'var(--text2)', flexShrink: 0 }}>· {ids.length}</span>
          {lead && <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead}</span>}
        </button>
        {teamId != null && ids.length > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" data-ph-capture="assignee_team_bulk"
            onClick={() => bulk(ids, teamId)} style={{ flexShrink: 0, color: 'var(--accent)', fontSize: 12 }}>
            {allOn ? t('assignee.unselectTeam') : t('assignee.selectTeam')}
          </button>
        )}
      </div>
    );
  };

  const row = (u, source, opts = {}) => (
    <PersonRow key={`${source}-${u.id}`} u={u} selected={selected.has(u.id)} isMe={u.id === user?.id}
      onToggle={() => toggle(u.id, source)} t={t} i18n={i18n} indent={opts.indent} sub={opts.sub} />
  );

  let body;
  if (!users.length) {
    body = <div style={{ padding: 16, fontSize: 13, color: 'var(--text2)' }}>{t('tasks.noTeam')}</div>;
  } else if (q) {
    // Поиск — плоский список: имя/телефон/email и название команды
    const teamName = new Map(teams.map((tm) => [tm.id, tm.name]));
    const hits = users.filter((u) => memberMatches(u, q)
      || (u.team_id && String(teamName.get(u.team_id) || '').toLocaleLowerCase().includes(q)));
    body = hits.length
      ? hits.map((u) => row(u, 'search', { sub: u.team_id ? `${roleText(t, i18n, u)} · ${teamName.get(u.team_id) || ''}` : undefined }))
      : <div style={{ padding: 16, fontSize: 13, color: 'var(--text2)' }}>{t('teams.searchEmpty')}</div>;
  } else {
    const parts = [];
    if (recentIds.length) {
      parts.push(<div key="rec-h" style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('assignee.recent')}</div>);
      for (const id of recentIds) parts.push(row(byId.get(id), 'recent', { sub: t('assignee.wasOnSite') }));
    }
    if (!teams.length) {
      for (const { m } of grouped.none) parts.push(row(m, 'list'));
    } else {
      for (const sct of grouped.sections) {
        const key = `t${sct.team.id}`;
        const ids = sct.rows.map((r) => r.m.id);
        const lead = sct.team.lead_name ? `${t('teams.leadLabel')}: ${sct.team.lead_name}` : '';
        parts.push(<div key={`${key}-h`}>{sectionHead(key, sct.team.name, ids, sct.team.id, lead)}</div>);
        if (isOpenSection(key)) for (const r of sct.rows) parts.push(row(r.m, 'team', { indent: true }));
      }
      if (grouped.none.length) {
        const ids = grouped.none.map((r) => r.m.id);
        parts.push(<div key="none-h">{sectionHead('none', t('teams.noTeamSection'), ids, null, '')}</div>);
        if (isOpenSection('none')) for (const r of grouped.none) parts.push(row(r.m, 'none', { indent: true }));
      }
    }
    body = parts;
  }

  const panelInner = (
    <>
      <div className="search-box" style={{ margin: 0, padding: 8, borderBottom: '1px solid var(--border)' }}>
        <span className="search-icon" style={{ left: 18 }}>🔍</span>
        <input ref={searchRef} type="search" className="form-input" value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Панель живёт внутри <form> Работы: Enter в поиске не должен её отправлять
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          onBlur={() => { if (query.trim()) track('assignee_picker_search', { length: query.trim().length }); }}
          placeholder={t('assignee.searchPlaceholder')}
          autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
          data-form-type="other" data-lpignore="true"
          style={{ ...FIELD, WebkitAppearance: 'none' }} />
      </div>
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, maxHeight: narrow ? 'none' : 340 }}>{body}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: 8, borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('assignee.selectedCount', { count: value.length })}</span>
        <button type="button" className="btn btn-primary btn-sm" data-ph-capture="assignee_picker_done" onClick={() => setOpen(false)}>
          {t('assignee.done')}
        </button>
      </div>
    </>
  );

  return (
    <div>
      {field}
      {open && !narrow && (
        <div ref={panelRef} style={{
          marginTop: 6, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
        }}>
          {panelInner}
        </div>
      )}
      {open && narrow && createPortal(
        <div {...backdropClose(() => setOpen(false))} style={{
          position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'flex-end',
          ...keyboardFitStyle(kb),
        }}>
          <div role="dialog" aria-label={t('assignee.sheetTitle')} style={{
            width: '100%', maxHeight: kb ? '100%' : '82dvh', background: 'var(--bg)', borderRadius: '14px 14px 0 0',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 4px' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{t('assignee.sheetTitle')}</span>
              <button type="button" className="btn btn-ghost btn-sm" aria-label={t('common.close')} onClick={() => setOpen(false)}>
                <Icon name="x" size={16} />
              </button>
            </div>
            {panelInner}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
